/**
 * Copyright (c) 2025-present, Bruno Carvalho de Araujo.
 * All rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in
 * the root directory of this source tree.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import uniqueId from 'lodash/uniqueId'
import { useMemo, type FC } from 'react'
import Badge from 'react-bootstrap/Badge'
import BaseButton from 'react-bootstrap/Button'
import CardBase from 'react-bootstrap/Card'
import ScrollBar from 'react-perfect-scrollbar'
import 'react-perfect-scrollbar/dist/css/styles.css'
import { useLocation, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

const Wrapper = styled.div`
  height: 98%;
  width: 100%;
  border-right: 1px solid #eaeaea;
`

const Card = styled(CardBase)`
  border-radius: 0;
  padding-bottom: 0px;
  background-color: transparent;
  border-top-color: transparent;
  border-right-color: transparent;
  border-left-color: transparent;

  .card-header {
    padding-top: 5px;
    padding-left: 0;
    padding-right: 0;
    border-top: none;
  }

  .card-body {
    padding: 0;
    border-top: none;
  }
`

const Button: FC<any> = styled(BaseButton)`
  padding-left: 20px;
  padding-right: 20px;
  text-align: left;
  border: none;
  background-color: transparent;
  color: inherit;
  line-height: 1.5;
  transition: all 0.3s;

  svg {
    margin-right: 10px;
  }

  &.active {
    background-color: #e9ecef;
    color: #000;
    font-weight: 600;
  }
`

export type SidebarRoute = {
  title: string
  path: string
  when?: boolean
  focusedIcon: any
  unfocusedIcon?: any
  color?: string
  badge?: number
  disabled?: boolean
}

export type MenuItem = {
  title?: string
  focusedIcon?: any
  unfocusedIcon?: any
  disabled?: boolean,
  when?: boolean,
  path?: string
  routes?: SidebarRoute[]
}

export type Props = {
  routes: MenuItem[]
}

/**
 * Sidebar is described here.
 *
 * @example
 * ```jsx
 * <Sidebar routes={someRoutes} />
 * ```
 *
 * @since 08/03/2025
 * @link https://react-styleguidist.js.org/docs/documenting.html
 */
const Sidebar: FC<Props> = ({ routes = [] }) => {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const currentRoutes = useMemo(() => {
    return routes.map(area => ({
      ...area,
      routes: area?.routes?.map(route => ({
        isActive: route.path === '/' ? pathname === route.path : pathname.startsWith(route.path),
        ...route
      }))
    }))
  }, [routes, pathname])

  return (
    <Wrapper>
      <ScrollBar className='sidebar-body' component="div">
        {currentRoutes.map(area => {
          const visibleAreas = area?.routes?.filter(route => route.when !== false) ?? []

          if (area.when === false || visibleAreas.length === 0) {
            return null
          }

          return (
            <Card key={area?.title ?? uniqueId()}>
              <Card.Header>
                <Card.Title style={{ paddingLeft: '20px', fontWeight: 'bold', opacity: 0.8 }}>
                  {area?.focusedIcon && <FontAwesomeIcon icon={area.focusedIcon} />}
                  {' '}
                  {area?.title}
                </Card.Title>
              </Card.Header>
              <Card.Body className="d-grid gap-2">
                {area?.routes?.map(route => {
                  if (route.when === false) {
                    return null
                  }

                  return (
                    <Button
                      key={route.path}
                      textAlign='left'
                      disabled={route.disabled}
                      active={route.isActive}
                      onClick={() => navigate(route.path)}
                      variant={pathname === route.path ? 'secondary' : 'outline-secondary'}
                    >
                      <FontAwesomeIcon icon={route.isActive ? route.focusedIcon : route.unfocusedIcon ?? route.focusedIcon} />
                      {' '}
                      {route.title}
                      {route.badge && (
                        <small>
                          {' '}
                          <Badge bg="secondary" pill>{route.badge}</Badge>
                        </small>
                      )}
                    </Button>
                  )
                })}
              </Card.Body>
            </Card>
          )
        })}
      </ScrollBar>
    </Wrapper>
  )
}

export default Sidebar
